import { useStore } from '@tanstack/react-form';
import { getFieldErrorId, mergeAriaIds } from '../../helpers/fieldAccessibility';
import { useFormContext } from './formContext';

export type FieldWithMeta = {
  name: string;
  state: { meta: { errors: unknown[]; isTouched: boolean } };
};

// aria-invalid / aria-describedby for the control, gated on the exact condition
// TanstackFieldError uses — the error id must not reference an unrendered element.
export const useTanstackFieldAria = (field: FieldWithMeta, describedBy?: string) => {
  const { shouldShow } = useTanstackFieldError(field);
  return {
    'aria-invalid': shouldShow || undefined,
    'aria-describedby': mergeAriaIds(describedBy, shouldShow ? getFieldErrorId(field.name) : undefined)
  };
};

export const useTanstackFieldError = (field: FieldWithMeta) => {
  const form = useFormContext();
  // Subscribed read — a bare form.state read here is only fresh by accident
  // (field meta changes happen to re-render this subtree on submit).
  const submitCount = useStore(form.store, (state) => state.submissionAttempts);
  const errors = field.state.meta.errors;
  const isTouched = field.state.meta.isTouched;
  const errorMessage = errors.length > 0 ? getErrorMessage(errors) : undefined;
  return { errorMessage, shouldShow: !!errorMessage && (isTouched || submitCount > 0) };
};

export const getErrorMessage = (errors: unknown[]): string | undefined => {
  for (const error of errors) {
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      return error.message;
    }
  }
  return undefined;
};
